// ActivityPage — "tweet for $1 credit" promo. Post a tweet with the verification
// code, paste the URL back, server verifies + credits $1.
//
// Endpoints (all JWT, on /reward/*):
//   GET  /reward/verification-code  — get/create code, returns { code, status, template, expiresAt, rewardAmount, hasClaimedReward }
//   POST /reward/verify-tweet       — body { tweetUrl } → credits balance, returns { success, message, rewardAmount?, currentBalance? }
//
// Two view states:
//   • signed-in    — fetch verification code, render reward + tweet wizard
//   • not signed-in — gate card pointing at /login

import React from 'react';
import { XPrim, XIcons } from '../primitives.jsx';
import { XShell } from '../shell.jsx';
import { XApi } from '../services.js';
import { useT } from '../i18n.jsx';

// Estimated unit economics for the "what $1 unlocks" calculator. These mirror the per-call /
// per-token rates seen on the Twitter, LLM, and search APIs in the catalog. Approximations,
// not source-of-truth — drives only the marketing copy.
const REWARD_USD = 1.00;
const PRICE_TWITTER_PER_CALL = 0.001;
const PRICE_SEARCH_PER_CALL  = 0.005;
const PRICE_LLM_PER_1M       = 1.00;

const ActivityPage = () => {
  const { navigate } = XShell.useRoute();
  const { user, authed, isEntity, refreshMe } = XShell.useAuth();
  const { toast } = XPrim.useToast();
  const { t } = useT();

  // info shape from /reward/verification-code:
  //   { code, status, template, expiresAt, rewardAmount, hasClaimedReward }
  const [info, setInfo] = React.useState(null);
  const [tweetUrl, setTweetUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [done, setDone] = React.useState(false);
  const [tweetCopied, setTweetCopied] = React.useState(false);

  React.useEffect(() => {
    // VIRTUAL accounts are server-side blocked (403). Don't fetch — show bind CTA instead.
    if (!authed || !isEntity) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await XApi.rewardService.getCode();
        if (cancelled) return;
        setInfo(data);
        // hasClaimedReward is the canonical "you already claimed" flag — but
        // until the controller serializes it (was missing from the response
        // shape pre-fix), fall back to status === 'COMPLETED' which is also
        // returned and equally definitive.
        const claimed = !!(data && (data.hasClaimedReward || data.status === 'COMPLETED'));
        if (claimed) setDone(true);
      } catch (e) { if (!cancelled) setErr(e.message || 'Could not load activity session'); }
    })();
    return () => { cancelled = true; };
  }, [authed, isEntity]);

  // `info.code` is the user's referralCode for new/PENDING users (backend
  // unified the verification code with referralCode). For users who claimed
  // before that unification, the COMPLETED row's verificationCode field
  // still holds whatever random code was generated then — which is fine,
  // since COMPLETED users hit the "done" branch and never see the chip /
  // tweet template anyway.
  const inviteCode = (info && info.code) || '';
  const tweetText = (info && info.template) || '';
  const tweetIntentUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;

  const handleCopyTweet = () => {
    if (!tweetText) return;
    try { navigator.clipboard.writeText(tweetText); setTweetCopied(true); setTimeout(() => setTweetCopied(false), 1500); } catch {}
  };

  // Hard-stop errors: anti-fraud rejections that mean "this path is closed,
  // no point in letting the user click Verify again." Tracked separately so
  // we can disable the submit button + render a more explanatory message.
  // Backend strings are matched on the Chinese text the rewardService surfaces;
  // both Chinese and English copy lives in the i18n bundle below.
  const [hardBlock, setHardBlock] = React.useState(null);  // null | 'twitterBound' | 'alreadyClaimed' | 'tweetUsed'
  const classifyServerError = (msg) => {
    const s = String(msg || '');
    if (s.includes('Twitter 账户已被') || s.toLowerCase().includes('already bound')) return 'twitterBound';
    if (s.includes('已经领取过') || s.toLowerCase().includes('already claimed')) return 'alreadyClaimed';
    if (s.includes('该推文已被使用过') || s.toLowerCase().includes('tweet has already')) return 'tweetUsed';
    return null;
  };

  const submit = async () => {
    setErr(null);
    if (!tweetUrl.trim()) { setErr(t('activity.errorEmpty')); return; }
    setBusy(true);
    try {
      const r = await XApi.rewardService.verifyTweet(tweetUrl.trim());
      if (r && r.success === false) throw new Error(r.message || 'Verification failed');
      setDone(true);
      toast(t('activity.done.title'));
      // Refresh /me so the topbar balance reflects the just-credited $1 without
      // the user having to hard-reload the page.
      try { await refreshMe?.(); } catch {}
    } catch (e) {
      const code = classifyServerError(e.message);
      setHardBlock(code);
      // For hard blocks we surface our own explanatory copy instead of the
      // raw backend string ("该 Twitter 账户已被其他用户绑定" reads as if some
      // OTHER user grabbed *the user's own* handle, which is misleading).
      if (code) setErr(t(`activity.error.${code}`));
      else setErr(e.message || 'Verification failed');
    }
    finally { setBusy(false); }
  };

  // Tiny stat card for the calculator block. Big number on top, label underneath.
  const Calc = ({ value, label }) => (
    <div style={{flex: 1, padding:'14px 16px', background:'var(--bg-2)', border:'1px solid var(--line)', borderRadius: 8, minWidth: 0}}>
      <div className="mono" style={{fontSize: 18, color:'var(--fg-0)', letterSpacing:'-0.02em'}}>{value}</div>
      <div className="micro-mono" style={{textTransform:'none', letterSpacing: 0, marginTop: 6, color:'var(--fg-3)'}}>{label}</div>
    </div>
  );

  return (
    <div className="activity-shell" style={{maxWidth: 720, margin:'0 auto', padding:'40px clamp(14px, 5vw, 32px) 80px'}}>
      <div className="row gap-2" style={{color:'var(--fg-3)', fontSize: 12, marginBottom: 14}}>
        <a onClick={() => navigate(authed ? 'console' : 'portal')} style={{cursor:'pointer'}}>{authed ? t('topbar.console') : 'Home'}</a>
        <XIcons.IconChevronRight size={11}/>
        <span style={{color:'var(--fg-1)'}}>{t('activity.crumb')}</span>
      </div>
      <h1 style={{margin:0, fontSize: 28, letterSpacing:'-0.02em', fontWeight: 500}}>{t('activity.title')}</h1>
      <p style={{color:'var(--fg-3)', fontSize: 14, marginTop: 8, lineHeight: 1.65, maxWidth: 540}}>{t('activity.subtitle')}</p>

      {/* Reward hero + calculator — only shown when the user can still claim.
          Once claimed (done === true), this pitch becomes redundant; the
          done-state card below carries the celebration UI. */}
      {!done && (
        <div className="card" style={{padding: 28, marginTop: 22, background:'linear-gradient(180deg, rgba(245,158,11,0.05) 0%, transparent 100%)', borderColor:'rgba(245,158,11,0.25)'}}>
          <div className="row gap-3" style={{alignItems:'baseline'}}>
            <div className="mono" style={{fontSize: 48, letterSpacing:'-0.03em', color:'var(--warn)'}}>{t('activity.reward.amount')}</div>
            <div style={{color:'var(--fg-3)', fontSize: 14}}>{t('activity.reward.label')}</div>
          </div>

          {/* Calculator */}
          <div style={{marginTop: 18}}>
            <div className="micro" style={{marginBottom: 10}}>{t('activity.calc.title')}</div>
            <div className="row gap-2" style={{flexWrap:'wrap'}}>
              <Calc value={`${Math.round(REWARD_USD / PRICE_TWITTER_PER_CALL).toLocaleString()}`} label={t('activity.calc.twitter')} />
              <Calc value={`${(REWARD_USD / PRICE_LLM_PER_1M).toFixed(1)}M`}                       label={t('activity.calc.llm')} />
              <Calc value={`${Math.round(REWARD_USD / PRICE_SEARCH_PER_CALL).toLocaleString()}`}   label={t('activity.calc.search')} />
            </div>
          </div>
        </div>
      )}

      {/* Gate: not signed in */}
      {!authed && (
        <div className="card" style={{padding: 24, marginTop: 16, textAlign:'center'}}>
          <p style={{color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, marginBottom: 14}}>{t('activity.gateBody')}</p>
          <button className="btn btn-primary" onClick={() => navigate('login')}>
            {t('activity.gateCta')} <XIcons.IconArrowRight size={13}/>
          </button>
        </div>
      )}

      {/* Gate: signed in but VIRTUAL — must bind X first (mirrors backend ForbiddenException) */}
      {authed && !isEntity && (
        <div className="card" style={{padding: 24, marginTop: 16, textAlign:'center', background:'rgba(245,158,11,0.04)', borderColor:'rgba(245,158,11,0.25)'}}>
          <div className="micro" style={{color:'var(--warn)', marginBottom: 8}}>{t('activity.bindGate.eyebrow')}</div>
          <p style={{color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, marginBottom: 14, maxWidth: 420, marginLeft:'auto', marginRight:'auto'}}>{t('activity.bindGate.body')}</p>
          <button className="btn btn-primary" onClick={() => navigate('bind')}>
            <XIcons.IconTwitter size={13}/> {t('activity.bindGate.cta')}
          </button>
        </div>
      )}

      {/* Already-claimed — full celebration card so the user clearly sees the
          activity is done, not just a small badge above the tweet wizard.
          Shows: big check + amount + (when available) the X handle they
          claimed with, then the two onward CTAs. */}
      {authed && isEntity && done && (
        <div className="card" style={{
          padding: 32, marginTop: 16, textAlign:'center',
          background: 'linear-gradient(180deg, rgba(46, 197, 121, 0.08) 0%, transparent 100%)',
          borderColor: 'rgba(46, 197, 121, 0.3)',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(46, 197, 121, 0.12)',
            border: '1px solid rgba(46, 197, 121, 0.4)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px',
          }}>
            <XIcons.IconCheck size={26} style={{color:'var(--ok)'}}/>
          </div>

          <div className="micro" style={{color:'var(--ok)', marginBottom: 8}}>{t('activity.done.title')}</div>

          <div className="row" style={{justifyContent:'center', alignItems:'baseline', gap: 6, marginBottom: 6}}>
            <span className="mono" style={{fontSize: 36, color:'var(--ok)', letterSpacing:'-0.03em', fontWeight: 500}}>
              +${(info && info.rewardAmount) ? Number(info.rewardAmount).toFixed(2) : '1.00'}
            </span>
            <span className="micro-mono" style={{textTransform:'none', letterSpacing: 0, color:'var(--fg-3)'}}>USD</span>
          </div>

          <p style={{color:'var(--fg-3)', fontSize: 13, lineHeight: 1.7, margin:'8px auto 20px', maxWidth: 380}}>{t('activity.done.body')}</p>

          <div className="row gap-2" style={{justifyContent:'center', flexWrap:'wrap'}}>
            <button className="btn btn-primary" onClick={() => navigate('console')}>
              {t('activity.done.console')} <XIcons.IconArrowRight size={13}/>
            </button>
            <button className="btn" onClick={() => navigate('market')}>
              <XIcons.IconLayers size={13}/> {t('activity.done.exploreApis')}
            </button>
          </div>
        </div>
      )}

      {/* Error / loading */}
      {authed && isEntity && !done && err && !info && (
        <div className="card" style={{padding: 16, marginTop: 16, color:'var(--err)', fontSize: 13}}>{err}</div>
      )}
      {authed && isEntity && !done && !info && !err && (
        <div className="card" style={{padding: 24, marginTop: 16, textAlign:'center'}}>
          <div className="spinner" style={{margin:'0 auto'}}/>
        </div>
      )}

      {/* Wizard: tweet + verify in one card. The verification code is the user's
          permanent referralCode (also serves as their invite code) — no session
          expiry, so the old "expired" branch is gone. */}
      {authed && isEntity && info && !done && (
        <div className="card" style={{padding: 24, marginTop: 16}}>
          {/* Step 1: tweet */}
          <div className="micro">{t('activity.tweet.eyebrow')}</div>

          {inviteCode && (
            <div style={{display:'inline-flex', alignItems:'center', gap: 8, padding:'5px 10px', borderRadius: 5, background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.3)', marginTop: 10}}>
              <span className="micro" style={{color:'var(--warn)', opacity: 0.85}}>{t('activity.tweet.code')}</span>
              <code className="mono" style={{fontSize: 13, color:'var(--warn)', letterSpacing:'0.06em', fontWeight: 600}}>{inviteCode}</code>
            </div>
          )}

          <textarea readOnly className="input mono" style={{marginTop: 10, minHeight: 96, fontSize: 12.5, lineHeight: 1.6}} value={tweetText}/>

          <div className="row gap-2" style={{marginTop: 10}}>
            <button className={`btn ${tweetCopied ? '' : 'btn-ghost'}`} style={{flex: 1, justifyContent:'center'}} onClick={handleCopyTweet}>
              {tweetCopied ? <><XIcons.IconCheck size={12} style={{color:'var(--ok)'}}/> {t('common.copied')}</> : <><XIcons.IconCopy size={12}/> {t('activity.tweet.copy')}</>}
            </button>
            <a className="btn btn-primary" style={{flex: 2, justifyContent:'center'}} href={tweetIntentUrl} target="_blank" rel="noreferrer">
              <XIcons.IconTwitter size={13}/> {t('activity.tweet.openX')}
            </a>
          </div>

          {/* Step 2: paste URL + verify (in same card — promo flow stays compact) */}
          <div className="micro" style={{marginTop: 22}}>{t('activity.verify.eyebrow')}</div>
          <input className="input mono" style={{marginTop: 10}}
            placeholder={t('activity.verify.placeholder')}
            value={tweetUrl}
            onChange={e => setTweetUrl(e.target.value)}/>
          {err && <div style={{marginTop: 10, color:'var(--err)', fontSize: 12}}>{err}</div>}
          <button className="btn btn-primary" style={{marginTop: 14, width:'100%', justifyContent:'center', height: 38}} disabled={busy || !tweetUrl.trim() || !!hardBlock} onClick={submit}>
            {busy
              ? <><div className="spinner"/> {t('activity.verify.verifying')}</>
              : <><XIcons.IconCheck size={13}/> {t('activity.verify.submit')}</>}
          </button>

          {/* Session-expires footer removed: the verification code is now the
              user's permanent referralCode (NEVER_EXPIRES on backend), but
              we were still feeding info.expiresAt (= 2099-12-31) through
              formatRelative which surfaced confusing "just now" / "expired"
              text. The new copy makes no expiry promise. */}
        </div>
      )}
    </div>
  );
};

export const XActivity = { ActivityPage };
export { ActivityPage };
export default ActivityPage;
